import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { unstable_batchedUpdates } from 'react-dom';
import { Modal, Spin } from 'antd';
import firebase from 'firebase/compat/app';
import packageJson from '../../package.json';
import PreQualFunnelLayout from './PreQualFunnelLayout';
import {
  buildLendAPIIframeSrc,
  getValidSessionFromURL,
  isProdTesting,
  isE2EProdTesting,
} from '../utils/lendAPIUtils';
import LendAPIIframe from '../shared/components/LendAPIIframe/LendAPIIframe';
import { validateLendAPIRoute, normalizeProductType } from '../utils/lendAPIRouteValidation';
import { captureFullQueryString } from '../utils/queryParamHelper';
import PageNotFound from './components/PageNotFound';
import LendAPISuccessPage from '../loan/components/LendAPISuccessPage';
import {
  isProduction,
  LEND_API_ORIGIN,
  LENDAPI_COMBINED_FUNNEL_FROM_PARAM,
  MY_APPLICATIONS_PATH,
  KAMELEOON_COMBINED_FUNNEL_LOADED,
  BOAT_TRADER_SOURCE_ID,
  BOATTRADER_ORIGIN,
  BOATTRADER_QA_ORIGIN,
  LENDAPI_APPLICATION_HEADER_VALUE_PROPS,
  LendApiCombinedPrequalTab,
} from '../constants';
import {
  extractBoatInfoAndSourceFromPayload,
  EnrichBoatInfoInput,
} from '../utils/lendAPIEnrichmentHelper';
import { extractImtIdFromBoatUrl } from '../utils/collateralUrlHelper';
import {
  enhancePayloadWithStoredData,
  applyStoredDataFallback,
  mapLendAPIToPrequalSchema,
  callLendAPIWebhookWithProductId,
  callLendAPIWebhookForCombinedFullApp,
  fetchLendAPIApprovalData,
  CombinedFullAppWebhookResult,
  LendAPIApprovalData,
  LendAPIPayload,
  LendAPIPostMessageEvent,
  storeLendAPIPrequalData,
} from '../utils/lendAPIPrequalHelper';
import {
  mapLendAPIToIterableStarted,
  mapLendAPIToIterableCompleted,
  mapLendAPIToFullAppStarted,
  mapLendAPIToFullAppCompleted,
} from '../utils/lendAPIIterableHelper';
import { sendCustomEventToIterable } from '../utils/iterable';
import { processResumeLink } from '../utils/processResumeLink';
import {
  resolveProductHooks,
  getProductConfigById,
  resolvePrequalCompleteForcedLoginConfig,
  resolveResumeLinkForcedLoginConfig,
  processPerformanceTracking,
  resolveGATrackingConfig,
} from '../utils/lendAPIConfigHelper';
import { normalizeTabName, isTabMatch } from '../utils/lendAPIGAHelper';
import { sendKameleoonGoalConversion } from '../utils/lendAPIKameleoonHelper';
import { LendAPIProductHooks } from '../lendAPIConfig.types';
import { useUser } from '../hooks/useUser';
import { useFirestore } from '../hooks/useFirestore';
import { useAuth } from '../hooks/useAuth';
import LendAPILoginModal from '../shared/components/LendAPILoginModal';
import { buildResumeLink } from '../utils/buildResumeLink';
import { buildSafeApplyHref } from '../utils/safeApplyHref';
import { resolvePortalSourceId } from '../utils/portalBranding';
import { recordFinanceIdentity } from '../utils/recordFinanceIdentity';
import { writeResumeCacheEntry, deleteResumeCacheEntry } from '../utils/resumeCache';
import LendAPIFunnelHeaderRight from '../shared/components/LendAPIFunnelHeaderRight';
import useHistory from '../hooks/useHistory';
import useLendAPILogin from '../hooks/useLendAPILogin';
import { shouldShowProgressBar } from './prequalProgressConfig';
import {
  COMBINED_PREQUAL_PROGRESS_CONFIG,
  COMBINED_FULLAPP_PROGRESS_CONFIG,
  getCombinedProgressPhase,
  isCombinedApplicationPortionTab,
} from './combinedProgressConfig';

const DYNAMIC_APP_INTERNAL_VERSION =
  process.env.REACT_APP_INTERNAL_VERSION || packageJson.version || '1.0.0';

interface RouteParams {
  productType: string;
  productId: string;
  productSlug: string;
}

const getPrequalIdFromLocalStorage = (): string | undefined => {
  try {
    const imtId = localStorage.getItem('current-lendapi-prequal-imtid');
    if (imtId) {
      const key = `prequal-${imtId}`;
      const data = localStorage.getItem(key);
      if (data) {
        const parsed = JSON.parse(data);
        return parsed.prequalApplicationId;
      }
    }
  } catch (error) {
    console.error('[LendAPI] Error retrieving prequalId from localStorage:', error);
  }
  return undefined;
};

// Mirrors storeLendAPIPrequalData's own NONBDP fallback so a resume-cache key
// resolved later in the funnel (after the AboutYou-Individual trigger already
// stored this) always matches the key written there.
const getStoredImtIdOrNonBdp = (): string => {
  try {
    return localStorage.getItem('current-lendapi-prequal-imtid') || 'NONBDP';
  } catch (error) {
    console.error('[LendAPI Combined][ResumeCache] Error reading stored imtId:', error);
    return 'NONBDP';
  }
};

// This funnel is reached via a portal's CDN-proxied /boat-loans/apply/* or
// /yacht-loans/apply/* path (see lendAPIConfig.json's portalCtaRedirects). See
// safeApplyHref.ts for why window.location can be corrupted to a bare
// '/apply/...' shape and needs correcting (per-portal) before use.
const getResumeLinkSafeHref = (): string => buildSafeApplyHref(window.location.href);

const processHooks = async (
  eventData: any,
  eventType: string | undefined,
  tabName: string | undefined,
  hooks: LendAPIProductHooks | undefined,
  processContactInfoHook: (payload: LendAPIPayload) => Promise<void>,
  processPrequalCompleteHook: (payload: LendAPIPayload) => Promise<void>,
  processFullAppContactInfoHook: (payload: LendAPIPayload) => Promise<void>,
  processFullAppCompleteHook: (payload: LendAPIPayload) => Promise<void>,
  processEnrichLendAPIApplicationHook: (payload: LendAPIPayload) => Promise<void>,
  processCollateralURLHook: (payload: LendAPIPayload) => Promise<void>
): Promise<void> => {
  if (!eventData || !eventData.payload || !hooks) {
    return;
  }

  // Each branch below is independently isolated: a throw in one hook must
  // never prevent a sibling hook sharing the same trigger tab from running
  // (e.g. the best-effort enrichLendAPIApplication branch must still fire
  // even if the unrelated prequalContactInfo hook throws first — a real risk
  // since it decodes a marketplace-supplied custom querystring param).
  if (
    hooks.prequalContactInfo &&
    eventType === hooks.prequalContactInfo.type &&
    isTabMatch(tabName || '', hooks.prequalContactInfo.triggerTab)
  ) {
    try {
      await processContactInfoHook(eventData.payload as LendAPIPayload);
    } catch (error) {
      console.error('[LendAPI Combined][processHooks] prequalContactInfo hook failed:', error);
    }
  }

  if (
    hooks.prequalComplete &&
    eventType === hooks.prequalComplete.type &&
    isTabMatch(tabName || '', hooks.prequalComplete.triggerTab)
  ) {
    try {
      await processPrequalCompleteHook(eventData.payload as LendAPIPayload);
    } catch (error) {
      console.error('[LendAPI Combined][processHooks] prequalComplete hook failed:', error);
    }
  }

  if (
    hooks.fullAppContactInfo &&
    eventType === hooks.fullAppContactInfo.type &&
    isTabMatch(tabName || '', hooks.fullAppContactInfo.triggerTab)
  ) {
    try {
      await processFullAppContactInfoHook(eventData.payload as LendAPIPayload);
    } catch (error) {
      console.error('[LendAPI Combined][processHooks] fullAppContactInfo hook failed:', error);
    }
  }

  if (
    hooks.fullAppComplete &&
    eventType === hooks.fullAppComplete.type &&
    isTabMatch(tabName || '', hooks.fullAppComplete.triggerTab)
  ) {
    try {
      await processFullAppCompleteHook(eventData.payload as LendAPIPayload);
    } catch (error) {
      console.error('[LendAPI Combined][processHooks] fullAppComplete hook failed:', error);
    }
  }

  if (
    hooks.enrichLendAPIApplication &&
    hooks.enrichLendAPIApplication.enabled !== false &&
    eventType === hooks.enrichLendAPIApplication.type &&
    isTabMatch(tabName || '', hooks.enrichLendAPIApplication.triggerTab)
  ) {
    try {
      await processEnrichLendAPIApplicationHook(eventData.payload as LendAPIPayload);
    } catch (error) {
      console.error(
        '[LendAPI Combined][processHooks] enrichLendAPIApplication hook failed:',
        error
      );
    }
  }

  if (
    hooks.processCollateralURL &&
    hooks.processCollateralURL.enabled !== false &&
    eventType === hooks.processCollateralURL.type &&
    isTabMatch(tabName || '', hooks.processCollateralURL.triggerTab)
  ) {
    try {
      await processCollateralURLHook(eventData.payload as LendAPIPayload);
    } catch (error) {
      console.error('[LendAPI Combined][processHooks] processCollateralURL hook failed:', error);
    }
  }
};

const LendAPICombinedFunnel: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { productType: urlProductType, productId, productSlug } = useParams<RouteParams>();

  const productConfig = useMemo(() => getProductConfigById(productId), [productId]);
  const productType = useMemo(() => normalizeProductType(urlProductType || ''), [urlProductType]);
  const prodTesting = useMemo(() => isProdTesting(captureFullQueryString()), []);
  const e2eProdTesting = useMemo(() => isE2EProdTesting(captureFullQueryString()), []);
  const prequalCompleteForcedLoginConfig = useMemo(
    () => resolvePrequalCompleteForcedLoginConfig(productType, productConfig),
    [productType, productConfig]
  );
  const resumeLinkForcedLoginConfig = useMemo(
    () => resolveResumeLinkForcedLoginConfig(productType, productConfig),
    [productType, productConfig]
  );
  const gaConfig = useMemo(
    () => resolveGATrackingConfig(productType, productConfig),
    [productType, productConfig]
  );

  const [submitting, setSubmitting] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const [webhookResult, setWebhookResult] = useState<CombinedFullAppWebhookResult | null>(null);
  const [showBreakoutOverlay, setShowBreakoutOverlay] = useState(false);
  const fullAppCompleteTriggered = useRef(false);
  const fullAppStartedTriggered = useRef(false);
  const funnelLoadedFired = useRef(false);
  const prequalCompleteTriggered = useRef(false);
  const enrichFiredRef = useRef(false);
  // TRIDENT-999: dedicated ref guard for the new processCollateralURL hook,
  // deliberately SEPARATE from enrichFiredRef — that ref is scoped to the
  // click-through boatInfo path (AboutYou-Individual) and must never block
  // this hook from firing on the Loan Info tab, and vice versa.
  const collateralUrlFiredRef = useRef(false);
  const phaseTransitionRelayed = useRef(false);
  // TRIDENT-977: BoatTrader embeds this funnel in a modal via ?embedded=modal.
  // Read once on mount — the route unmounts this component on navigation, so
  // the query string cannot change under it.
  const isEmbeddedModalRef = useRef(
    new URLSearchParams(window.location.search).get('embedded') === 'modal'
  );
  const sourceIdFromUrlRef = useRef(
    resolvePortalSourceId(window.location.search, window.location.hostname)
  );

  const [currentTab, setCurrentTab] = useState<string>('');
  const [lastKnownTab, setLastKnownTab] = useState<string>('');

  const storedCustomRef = useRef<string>('');
  const storedLoanPurposeRef = useRef<string>('');
  const storedPurchasePriceRef = useRef<string>('');
  const storedApprovalOddsRef = useRef<number | undefined>(undefined);
  const storedApprovalOutcomeRef = useRef<string | undefined>(undefined);
  const storedBuyingStageRef = useRef<string | undefined>(undefined);
  const approvalFetchInProgressRef = useRef<Promise<LendAPIApprovalData> | null>(null);
  const approvalFetchCompleteRef = useRef<boolean>(false);

  const urlSessionId = getValidSessionFromURL();

  // TRIDENT-928: a resume-link open (?session=<uuid>) may need its 30-day TTL
  // reset by the AWS /processResumeLink endpoint before the iframe loads. While
  // that check is in flight we withhold the iframe behind a spinner. On a fresh
  // start (no session param) there is nothing to check, so the iframe renders
  // immediately. The check always fails open — the spinner never blocks the
  // funnel even if the endpoint errors or times out.
  const [resumeCheckPending, setResumeCheckPending] = useState<boolean>(Boolean(urlSessionId));
  const resumeCheckStarted = useRef(false);

  useEffect(() => {
    if (!urlSessionId || resumeCheckStarted.current) {
      return undefined;
    }
    resumeCheckStarted.current = true;
    let cancelled = false;
    const clearGate = () => {
      if (!cancelled) {
        setResumeCheckPending(false);
      }
    };
    processResumeLink({ appId: urlSessionId, prodTesting }).then(clearGate).catch(clearGate);
    return () => {
      cancelled = true;
    };
  }, [urlSessionId, prodTesting]);

  const iframeSrc = useMemo(
    () => buildLendAPIIframeSrc(productSlug || '', urlSessionId),
    [productSlug, urlSessionId]
  );

  const updateTabState = useCallback((tabName: string) => {
    setCurrentTab(tabName);
    const phase = getCombinedProgressPhase(tabName);
    if (phase) {
      setLastKnownTab(tabName);
    }
  }, []);

  const progressPhase =
    getCombinedProgressPhase(currentTab) || getCombinedProgressPhase(lastKnownTab);
  const activeProgressConfig =
    progressPhase === 'fullApp'
      ? COMBINED_FULLAPP_PROGRESS_CONFIG
      : COMBINED_PREQUAL_PROGRESS_CONFIG;
  const showProgressBar =
    !!currentTab && !!progressPhase && shouldShowProgressBar(currentTab, activeProgressConfig);
  const isApplicationPortion =
    isCombinedApplicationPortionTab(currentTab) || isCombinedApplicationPortionTab(lastKnownTab);

  useEffect(() => {
    console.log(`Dynamic App Version: ${DYNAMIC_APP_INTERNAL_VERSION}`);
    console.log('[LendAPICombinedFunnel] iframeSrc', iframeSrc);
    if (
      !isProduction() &&
      !isProdTesting(captureFullQueryString()) &&
      productConfig?.tags?.includes('prodTestingInQA')
    ) {
      console.error(
        `[LendAPICombinedFunnel] Production funnel loaded in QA without prodTesting=true. ` +
          `Add ?prodTesting=true to the URL to use production LendAPI products in QA.`
      );
    }
  }, [iframeSrc, productConfig]);

  useEffect(() => {
    if (!funnelLoadedFired.current) {
      funnelLoadedFired.current = true;
      sendKameleoonGoalConversion(KAMELEOON_COMBINED_FUNNEL_LOADED, 'combined');
    }
  }, []);

  // Auth-related state
  const [showForcedLoginModal, setShowForcedLoginModal] = useState(false);
  const [isResumeLogin, setIsResumeLogin] = useState(false);
  const [isBlurred, setIsBlurred] = useState(false);
  const prequalCompleteForcedLoginTriggered = useRef(false);
  const sessionLoginTriggered = useRef(false);
  const submittedCheckDone = useRef(false);

  const user = useUser();
  const firestore = useFirestore();
  const auth = useAuth();
  const history = useHistory();

  const isResumeLinkRedirect = useCallback(() => sessionLoginTriggered.current, []);
  const onResumeLinkRedirect = useCallback(() => {
    history.push(`${MY_APPLICATIONS_PATH}?from=${LENDAPI_COMBINED_FUNNEL_FROM_PARAM}`);
  }, [history]);
  const onLoginHandled = useCallback(() => {
    setShowForcedLoginModal(false);
    setIsBlurred(false);
  }, []);

  const {
    hasSession,
    sessionIdRef,
    storedEmailRef,
    storedFirstNameRef,
    storedLastNameRef,
    storedRawCustomRef,
    loanCreationInProgress,
    handleLoginSuccess,
    captureSessionFromPayload,
  } = useLendAPILogin({
    auth,
    firestore,
    urlSessionId,
    isResumeLinkRedirect,
    onResumeLinkRedirect,
    onLoginHandled,
  });

  const isAuthenticated = !!user && !user.isAnonymous;

  const isLeadSubmittedTriggerTab = useCallback(
    (tabName: string): boolean => {
      const triggerTabs = gaConfig?.leadSubmittedEvent?.triggerTabs;
      if (!gaConfig?.leadSubmittedEvent?.enabled || !triggerTabs?.length) return false;
      return isTabMatch(tabName, triggerTabs);
    },
    [gaConfig]
  );

  const ensureApprovalDataLoaded = useCallback(async (): Promise<void> => {
    // Once the fetch has resolved (with any payload, including {}), don't
    // re-await — LendAPI returns the same data for the session, and a partial
    // response (e.g. only approvalOdds) can never be filled in by re-awaiting.
    if (approvalFetchCompleteRef.current) return;

    // Skip the GCP round-trip when both approval refs are already populated.
    // This happens whenever (a) the lead_submitted IIFE's prior fetch on this
    // mount already wrote them, or (b) the postMessage handler captured them
    // from a formSubmission payload (the resume-link path). In either case
    // the values come from LendAPI directly, so re-fetching is wasted work.
    if (
      storedApprovalOddsRef.current !== undefined &&
      storedApprovalOutcomeRef.current !== undefined
    ) {
      approvalFetchCompleteRef.current = true;
      return;
    }

    if (!sessionIdRef.current) return;

    if (!approvalFetchInProgressRef.current) {
      approvalFetchInProgressRef.current = fetchLendAPIApprovalData(
        sessionIdRef.current,
        isProduction(),
        prodTesting,
        e2eProdTesting
      );
    }

    // fetchLendAPIApprovalData always resolves with {} on any failure, so this
    // await never throws. No try/catch needed.
    const data = await approvalFetchInProgressRef.current;
    if (data && typeof data.approvalOdds === 'number') {
      storedApprovalOddsRef.current = data.approvalOdds;
    }
    if (data && typeof data.approvalOutcome === 'string' && data.approvalOutcome.length > 0) {
      storedApprovalOutcomeRef.current = data.approvalOutcome;
    }
    approvalFetchCompleteRef.current = true;
  }, [prodTesting, e2eProdTesting, sessionIdRef]);

  // TRIDENT-971: BoatTrader-sourced-only, best-effort, once-per-session enrichment
  // of the LendAPI application with normalized boat/engine identity for the JD
  // Power boat-details prefill. Deliberately its OWN dedicated hook — NOT routed
  // through processContactInfoHook (which stores prequal data to localStorage) or
  // processFullAppContactInfoHook (Iterable-only) — even though it shares the same
  // triggerTab as processContactInfoHook in lendAPIConfig.json.
  const processEnrichLendAPIApplicationHook = useCallback(
    async (payload: LendAPIPayload) => {
      const { boatInfo, source } = extractBoatInfoAndSourceFromPayload(payload);

      if (source !== BOAT_TRADER_SOURCE_ID || !boatInfo) {
        console.log(
          '[LendAPI Combined][Enrichment] Skipping - not a BoatTrader-sourced boatInfo payload',
          { hasBoatInfo: !!boatInfo, source }
        );
        return;
      }

      const applicationId = payload.session;
      if (!applicationId) {
        console.log(
          '[LendAPI Combined][Enrichment] Skipping - no session/applicationId on payload'
        );
        return;
      }

      if (enrichFiredRef.current) {
        return;
      }
      enrichFiredRef.current = true;

      try {
        const enrichLendAPIApplication = firebase
          .functions()
          .httpsCallable('enrichLendAPIApplication');
        await enrichLendAPIApplication({
          applicationId,
          boatInfo: boatInfo as EnrichBoatInfoInput,
          productType,
          source,
          prodTesting,
        });
        console.log('[LendAPI Combined][Enrichment] enrichLendAPIApplication callable invoked');
      } catch (error) {
        console.warn(
          '[LendAPI Combined][Enrichment] enrichLendAPIApplication callable failed:',
          error
        );
      }
    },
    [productType, prodTesting]
  );

  // TRIDENT-999: fires on the Loan Info tab's formSubmission whenever a
  // BoatTrader listing URL (boat_url) is present. Uses collateralUrlFiredRef
  // (NOT enrichFiredRef) so it fires independently of the click-through hook
  // above. Always calls through with boatUrl so the backend can persist it
  // unconditionally; imtId is included only when it's newly-known (absent or
  // different from the click-through's own imtId decoded from the SAME
  // payload) — this lets the backend skip a redundant listing lookup when the
  // user's typed/prefilled URL resolves to the same boat they already clicked
  // through on.
  const processCollateralURLHook = useCallback(
    async (payload: LendAPIPayload) => {
      const boatUrl = typeof payload.boat_url === 'string' ? payload.boat_url : undefined;
      if (!boatUrl) {
        return;
      }

      const applicationId = payload.session;
      if (!applicationId) {
        console.log('[processCollateralURL] Skipping - no session/applicationId on payload');
        return;
      }

      if (collateralUrlFiredRef.current) {
        return;
      }
      collateralUrlFiredRef.current = true;

      const { boatInfo: clickThroughBoatInfo, source } =
        extractBoatInfoAndSourceFromPayload(payload);
      const clickThroughImtId = (clickThroughBoatInfo as { imtId?: number | string } | null)?.imtId;
      const extractedImtId = extractImtIdFromBoatUrl(boatUrl);

      if (!extractedImtId) {
        console.log(
          '[processCollateralURL] no-op: off-domain/no IMT id — persisting boatUrl only',
          {
            boatUrl,
          }
        );
      }

      const imtIdMatchesClickThrough =
        !!extractedImtId &&
        clickThroughImtId !== undefined &&
        clickThroughImtId !== null &&
        String(clickThroughImtId) === extractedImtId;

      try {
        const enrichLendAPIApplication = firebase
          .functions()
          .httpsCallable('enrichLendAPIApplication');
        await enrichLendAPIApplication({
          applicationId,
          boatUrl,
          productType,
          source,
          prodTesting,
          ...(extractedImtId && !imtIdMatchesClickThrough && { imtId: extractedImtId }),
        });
        console.log('[processCollateralURL] success', {
          boatUrl,
          imtId: extractedImtId,
          deduped: imtIdMatchesClickThrough,
        });
      } catch (error) {
        console.warn('[processCollateralURL] lookup failed', error);
      }
    },
    [productType, prodTesting]
  );

  const processContactInfoHook = useCallback(
    async (payload: LendAPIPayload) => {
      const mappedData = mapLendAPIToPrequalSchema(payload);

      if (mappedData.email) storedEmailRef.current = mappedData.email;
      if (mappedData.firstName) storedFirstNameRef.current = mappedData.firstName;
      if (mappedData.lastName) storedLastNameRef.current = mappedData.lastName;
      if (mappedData.customParams) storedCustomRef.current = mappedData.customParams;
      const rawCustom = payload.app_url_params?.custom || payload.custom;
      if (rawCustom) storedRawCustomRef.current = rawCustom;

      // Extract session from the payload for auth flow
      captureSessionFromPayload(payload);

      const { imtId } = mappedData;
      storeLendAPIPrequalData(mappedData, imtId);

      // TRIDENT-977: resume cache — only meaningful for the embedded-modal
      // flow (the only path that ever needs to resume an abandoned session).
      if (isEmbeddedModalRef.current && sessionIdRef.current) {
        const resumeCacheKey = imtId && String(imtId).trim() ? String(imtId).trim() : 'NONBDP';
        writeResumeCacheEntry(
          resumeCacheKey,
          'prequal',
          buildResumeLink(getResumeLinkSafeHref(), sessionIdRef.current, mappedData.email),
          sessionIdRef.current
        );
      }

      if (mappedData.email) {
        try {
          const startedEvent = mapLendAPIToIterableStarted(payload, mappedData);
          await sendCustomEventToIterable({ ...startedEvent, e2eProdTesting });
          console.log('[LendAPI Combined][Iterable] PreQualStarted event sent successfully');
        } catch (error) {
          console.error('[LendAPI Combined][Iterable] Error sending PreQualStarted event:', error);
        }
      } else {
        console.warn(
          '[LendAPI Combined][Iterable][processContactInfoHook] Skipping event - missing required data:',
          {
            hasEmail: !!mappedData.email,
          }
        );
      }
    },
    [
      e2eProdTesting,
      captureSessionFromPayload,
      sessionIdRef,
      storedEmailRef,
      storedFirstNameRef,
      storedLastNameRef,
      storedRawCustomRef,
    ]
  );

  const processFullAppContactInfoHook = useCallback(
    async (payload: LendAPIPayload) => {
      if (fullAppStartedTriggered.current) {
        console.warn('[LendAPI Combined][FullApp] Duplicate FullAppStarted trigger ignored');
        return;
      }

      const applicationId = payload.session;
      const prequalId = getPrequalIdFromLocalStorage();
      const email = payload.email || new URLSearchParams(window.location.search).get('email') || '';

      if (applicationId && email) {
        fullAppStartedTriggered.current = true;
        try {
          await ensureApprovalDataLoaded();
          const resumeLink = payload.session
            ? buildResumeLink(getResumeLinkSafeHref(), payload.session, email)
            : undefined;
          const enrichedPayload = { ...payload, email };
          const eventData = mapLendAPIToFullAppStarted(
            enrichedPayload,
            applicationId,
            prequalId,
            resumeLink,
            storedApprovalOddsRef.current,
            storedApprovalOutcomeRef.current,
            storedBuyingStageRef.current
          );
          const result = await sendCustomEventToIterable({ ...eventData, e2eProdTesting });
          if (result.success) {
            console.log(
              '[LendAPI Combined][FullApp][Iterable] FullAppStarted event sent successfully'
            );
          } else {
            console.error(
              '[LendAPI Combined][FullApp][Iterable] FullAppStarted event failed to send'
            );
          }
        } catch (error) {
          console.error(
            '[LendAPI Combined][FullApp][Iterable] Error sending FullAppStarted event:',
            error
          );
        }
      } else {
        console.warn(
          '[LendAPI Combined][FullApp][Iterable] Skipping FullAppStarted - missing required data:',
          { hasApplicationId: !!applicationId, hasEmail: !!email }
        );
      }
    },
    [ensureApprovalDataLoaded, e2eProdTesting]
  );

  const processPrequalCompleteHook = useCallback(
    async (payload: LendAPIPayload) => {
      if (prequalCompleteTriggered.current) {
        console.warn('[LendAPI Combined][PrequalComplete] Duplicate trigger ignored');
        return;
      }
      prequalCompleteTriggered.current = true;

      const storedData = {
        email: storedEmailRef.current,
        firstName: storedFirstNameRef.current,
        lastName: storedLastNameRef.current,
        customParams: storedCustomRef.current,
      };

      const enhancedPayload = enhancePayloadWithStoredData(payload, storedData);
      const mappedData = mapLendAPIToPrequalSchema(enhancedPayload);
      const finalMappedData = applyStoredDataFallback(mappedData, storedData);

      try {
        const applicationId = finalMappedData.prequalApplicationId;
        if (applicationId && productId) {
          console.log(
            '[LendAPI Combined][Lead Creation] Triggering webhook processing for applicationId:',
            applicationId
          );

          const numericProductId = parseInt(productId, 10);
          callLendAPIWebhookWithProductId(
            applicationId,
            numericProductId,
            isProduction(),
            prodTesting,
            e2eProdTesting
          )
            .then(() => {
              recordFinanceIdentity('lead');
            })
            .catch((webhookError: Error) => {
              console.error('[LendAPI Combined][Lead Creation] Webhook call failed:', webhookError);
            });

          console.log('[LendAPI Combined][Lead Creation] Webhook processing triggered');
        } else {
          console.warn(
            '[LendAPI Combined][Lead Creation] No applicationId or productId found, skipping webhook trigger'
          );
        }
      } catch (webhookError) {
        console.error(
          '[LendAPI Combined][Lead Creation] Error in webhook trigger block:',
          webhookError
        );
      }

      if (finalMappedData.prequalApplicationId && finalMappedData.email) {
        try {
          const completedEvent = mapLendAPIToIterableCompleted(enhancedPayload, finalMappedData);

          const resumeLink = sessionIdRef.current
            ? buildResumeLink(
                getResumeLinkSafeHref(),
                sessionIdRef.current,
                storedEmailRef.current || undefined
              )
            : undefined;

          const finalEventData = {
            ...completedEvent,
            ...(resumeLink && {
              eventData: {
                ...completedEvent.eventData,
                resumeLink,
              },
            }),
            isLendAPI: true,
            applicationId: finalMappedData.prequalApplicationId,
            prodTesting,
            e2eProdTesting,
          };

          const result = await sendCustomEventToIterable(finalEventData);

          if (result.success) {
            console.log('[LendAPI Combined][Iterable] PreQualCompleted event sent successfully');
          } else {
            console.error('[LendAPI Combined][Iterable] PreQualCompleted event failed to send');
          }
        } catch (error) {
          console.error(
            '[LendAPI Combined][Iterable] Error sending PreQualCompleted event:',
            error
          );
        }
      } else {
        console.warn(
          '[LendAPI Combined][Iterable][processPrequalCompleteHook] Skipping event - missing required data:',
          {
            hasApplicationId: !!finalMappedData.prequalApplicationId,
            hasEmail: !!finalMappedData.email,
          }
        );
      }
    },
    [
      productId,
      prodTesting,
      e2eProdTesting,
      sessionIdRef,
      storedEmailRef,
      storedFirstNameRef,
      storedLastNameRef,
    ]
  );

  const processFullAppCompleteHook = useCallback(
    async (payload: LendAPIPayload) => {
      if (fullAppCompleteTriggered.current) {
        console.warn('[LendAPI Combined][FullApp] Duplicate trigger ignored');
        return;
      }

      const applicationId = payload.session;
      if (!applicationId || !productId) {
        console.warn(
          '[LendAPI Combined][FullApp] No applicationId or productId found, skipping webhook trigger',
          { hasApplicationId: !!applicationId, hasProductId: !!productId }
        );
        return;
      }
      fullAppCompleteTriggered.current = true;

      // TRIDENT-977: nothing left to resume once the application is submitted.
      // Clear this tab's own copy (a no-op if this tab never had an entry).
      deleteResumeCacheEntry(getStoredImtIdOrNonBdp());
      // Completion reached via the phase-transition breakout happens in a NEW
      // tab opened via window.open() — that tab's sessionStorage is a
      // disconnected CLONE taken at open time, so deleting only the local
      // copy above never reaches window.opener's storage, which is the one
      // portal will actually re-read on a later click. Reach back through
      // window.opener (same-origin: the CDN-proxied URL this tab loaded) and
      // clear it there too. Best-effort — window.opener can legitimately be
      // null (e.g. direct navigation, no breakout involved) or inaccessible.
      if (window.opener) {
        try {
          deleteResumeCacheEntry(getStoredImtIdOrNonBdp(), window.opener.sessionStorage);
        } catch (error) {
          console.error(
            '[LendAPI Combined][ResumeCache] Could not clear opener resume-cache entry:',
            error
          );
        }
      }

      const prequalId = getPrequalIdFromLocalStorage();
      const email = payload.email || new URLSearchParams(window.location.search).get('email') || '';
      if (email) {
        try {
          const enrichedPayload = { ...payload, email };
          const iterableEvent = mapLendAPIToFullAppCompleted(
            enrichedPayload,
            applicationId,
            prequalId
          );
          sendCustomEventToIterable({ ...iterableEvent, e2eProdTesting })
            .then((result) => {
              if (result.success) {
                console.log(
                  '[LendAPI Combined][FullApp][Iterable] FullAppCompleted event sent successfully'
                );
              } else {
                console.error(
                  '[LendAPI Combined][FullApp][Iterable] FullAppCompleted event failed to send'
                );
              }
            })
            .catch((iterableError) => {
              console.error(
                '[LendAPI Combined][FullApp][Iterable] Error sending FullAppCompleted event:',
                iterableError
              );
            });
        } catch (error) {
          console.error(
            '[LendAPI Combined][FullApp][Iterable] Error preparing FullAppCompleted event:',
            error
          );
        }
      }

      console.log(
        '[LendAPI Combined][FullApp] Triggering fullAppSubmitted webhook for applicationId:',
        applicationId
      );

      setSubmitting(true);

      try {
        const numericProductId = parseInt(productId, 10);
        const result = await callLendAPIWebhookForCombinedFullApp(
          applicationId,
          numericProductId,
          isProduction(),
          prodTesting,
          e2eProdTesting
        );

        console.log('[LendAPI Combined][FullApp] Webhook response received:', result);

        recordFinanceIdentity('application');

        unstable_batchedUpdates(() => {
          setWebhookResult(result);
          setSubmitting(false);
          setApplicationSubmitted(true);
        });
      } catch (error) {
        console.error(
          '[LendAPI Combined][FullApp] Webhook error, showing success page without documents:',
          error
        );

        unstable_batchedUpdates(() => {
          setWebhookResult(null);
          setSubmitting(false);
          setApplicationSubmitted(true);
        });
      } finally {
        // Hydrate approval refs before firing the Submitted GA event so it can
        // include approval_odds / approval_outcome alongside transaction_id.
        // Safe to await here — this hook is already async and the user is on
        // the Submitted screen, no synchronous control flow depends on it.
        await ensureApprovalDataLoaded();
        // Note: storedPurchasePriceRef is a string but GA Enhanced Ecommerce's
        // revenue/price fields are typed `number`, so we deliberately omit
        // purchase_price here. loan_purpose is omitted for symmetry — the
        // Submitted-tab event has not historically carried these fields.
        processPerformanceTracking('Submitted', productConfig, productType, {
          session: applicationId,
          ...(storedApprovalOddsRef.current !== undefined && {
            approval_odds: storedApprovalOddsRef.current,
          }),
          ...(storedApprovalOutcomeRef.current !== undefined && {
            approval_outcome: storedApprovalOutcomeRef.current,
          }),
        });
        console.log('[LendAPI Combined][FullApp] Tracking events fired for Submitted');
      }
    },
    [productId, prodTesting, e2eProdTesting, productConfig, productType, ensureApprovalDataLoaded]
  );

  // URL-based immediate login trigger (Combined funnel only)
  // Waits for useUser() to resolve (user !== null) before checking auth state,
  // avoiding a race condition where auth.currentUser is null on mount before
  // onAuthStateChanged fires from persistence restoration.
  useEffect(() => {
    if (urlSessionId && !sessionLoginTriggered.current && user !== null) {
      console.log('[LendAPICombined] Session extracted from URL:', urlSessionId);
      sessionIdRef.current = urlSessionId;
      const userIsAuthenticated = !user.isAnonymous;
      if (!userIsAuthenticated && resumeLinkForcedLoginConfig?.enabled) {
        sessionLoginTriggered.current = true;
        prequalCompleteForcedLoginTriggered.current = true;
        setIsResumeLogin(true);

        const urlEmail = new URLSearchParams(window.location.search).get('email');
        if (urlEmail) {
          storedEmailRef.current = urlEmail;
          console.log('[LendAPICombined] Email extracted from resume link URL');
        }

        setShowForcedLoginModal(true);
        if (resumeLinkForcedLoginConfig?.blur) {
          setIsBlurred(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Redirect to MyApps if the resume link's loan is already submitted
  useEffect(() => {
    if (!urlSessionId || !isAuthenticated || submittedCheckDone.current) return;
    submittedCheckDone.current = true;

    const loanId = `lendapi-${urlSessionId}`;
    firestore
      .collection('loans')
      .doc(loanId)
      .get()
      .then((doc) => {
        if (doc.exists && doc.data()?.workflow === 'submitted') {
          console.log('[LendAPICombined] Loan already submitted, redirecting to MyApps');
          history.push(`${MY_APPLICATIONS_PATH}?from=${LENDAPI_COMBINED_FUNNEL_FROM_PARAM}`);
        }
      })
      .catch((error) => {
        console.error('[LendAPICombined] Error checking loan status:', error);
      });
  }, [urlSessionId, isAuthenticated, firestore, history]);

  useEffect(() => {
    const handlePostMessage = async (event: LendAPIPostMessageEvent) => {
      if (event.origin !== LEND_API_ORIGIN) return;

      try {
        if (event.data?.type !== 'dimensionChange') {
          console.log(
            `[LendAPI Combined][handleMessage] | Received event of type ${event.data?.type} for tab ${event.data?.payload?.tab_name}`,
            JSON.stringify(event.data)
          );
        }

        const eventType = event.data?.type;
        const tabName = event.data?.payload?.tab_name;
        const hooks = resolveProductHooks(productType, productConfig);

        // Capture the session BEFORE the phase-transition relay check below so the
        // relay's resumeLink is built from the freshest sessionIdRef — otherwise a
        // session that first arrives on the very event that also triggers the
        // fullApp transition would be missed (captureSessionFromPayload is a no-op
        // once sessionIdRef.current is already set, so moving it earlier is safe).
        captureSessionFromPayload(event.data?.payload);

        if (tabName && (eventType === 'pageLoad' || eventType === 'formSubmission')) {
          updateTabState(tabName);

          // TRIDENT-1177: While embedded in a BoatTrader modal, tell the parent whether
          // the current step is the landing page so it can show/hide the modal title.
          if (isEmbeddedModalRef.current && eventType === 'pageLoad') {
            const targetOrigin = isProduction() ? BOATTRADER_ORIGIN : BOATTRADER_QA_ORIGIN;
            window.parent.postMessage(
              {
                type: 'trident:stepChange',
                isLandingPage: tabName === LendApiCombinedPrequalTab.LOAN_PURPOSE_NA,
              },
              targetOrigin
            );
          }

          // TRIDENT-977: When embedded inside a BoatTrader modal, the instant the
          // funnel classification transitions into the Full App phase (right after
          // "Finalize Your Application" is clicked), relay a one-shot postMessage
          // to the parent window so it can hand off / resume the flow outside the
          // modal, and show a full-cover overlay while that handoff happens.
          if (
            isEmbeddedModalRef.current &&
            !phaseTransitionRelayed.current &&
            getCombinedProgressPhase(tabName) === 'fullApp'
          ) {
            phaseTransitionRelayed.current = true;
            setShowBreakoutOverlay(true);

            if (!sessionIdRef.current) {
              console.warn(
                '[LendAPI Combined][PhaseTransition] Relaying phase transition with no sessionId — resumeLink will be unusable'
              );
            }

            const resumeLink = buildResumeLink(
              getResumeLinkSafeHref(),
              sessionIdRef.current,
              storedEmailRef.current
            );
            const targetOrigin = isProduction() ? BOATTRADER_ORIGIN : BOATTRADER_QA_ORIGIN;
            console.log(
              '[LendAPI Combined][PhaseTransition] Relaying trident:phaseTransition to parent',
              targetOrigin
            );
            window.parent.postMessage(
              { type: 'trident:phaseTransition', resumeLink },
              targetOrigin
            );

            if (sessionIdRef.current) {
              writeResumeCacheEntry(
                getStoredImtIdOrNonBdp(),
                'fullApp',
                resumeLink,
                sessionIdRef.current
              );
            }
          }
        }

        if (eventType === 'formSubmission' && event.data?.payload) {
          const p = event.data.payload;
          if (p.loan_purpose) storedLoanPurposeRef.current = p.loan_purpose;
          if (p.purchase_price != null) storedPurchasePriceRef.current = String(p.purchase_price);
          // Backfill approval refs from any formSubmission payload that carries them.
          // The LendAPI iframe includes approval_odds / approval_outcome on every
          // formSubmission from the results tab onward. This makes resume-link users —
          // who skip the pageLoad-driven cloud function fetch on the results tab — still
          // have populated refs by the time the Submitted formSubmission triggers
          // processFullAppCompleteHook in the same handler.
          if (typeof p.approval_odds === 'number') {
            storedApprovalOddsRef.current = p.approval_odds;
          }
          if (typeof p.approval_outcome === 'string' && p.approval_outcome.length > 0) {
            storedApprovalOutcomeRef.current = p.approval_outcome;
          }
          if (typeof p.buying_stage === 'string' && p.buying_stage.length > 0) {
            storedBuyingStageRef.current = p.buying_stage;
          }
        }

        // Process hooks (Iterable events, LendAPI Webhook trigger)
        // Fire-and-forget: do NOT await — async hooks (e.g. sendCustomEventToIterable)
        // would block the login modal from appearing until the network call completes.
        processHooks(
          event.data,
          eventType,
          tabName,
          hooks,
          processContactInfoHook,
          processPrequalCompleteHook,
          processFullAppContactInfoHook,
          processFullAppCompleteHook,
          processEnrichLendAPIApplicationHook,
          processCollateralURLHook
        ).catch((hookError) => {
          console.error('[LendAPI Combined] Error in processHooks:', hookError);
        });

        // Handle pageLoad events (GA and Kameleoon tracking)
        // Must run BEFORE login modal trigger — setState calls in React 17 (outside
        // React event handlers) cause synchronous re-renders that can interrupt execution.
        // Skip "Submitted" tab tracking here — it's already handled in processFullAppCompleteHook's finally block
        if (
          event.data &&
          event.data.type === 'pageLoad' &&
          event.data.payload &&
          event.data.payload.tab_name &&
          normalizeTabName(event.data.payload.tab_name) !== normalizeTabName('Submitted')
        ) {
          window.scrollTo(0, 0);
          const eventPayload = event.data.payload;
          const trackingTabName = eventPayload.tab_name;
          const buildEnhancedPayload = () => ({
            ...eventPayload,
            ...(!eventPayload.loan_purpose &&
              storedLoanPurposeRef.current && {
                loan_purpose: storedLoanPurposeRef.current,
              }),
            ...(!eventPayload.purchase_price &&
              storedPurchasePriceRef.current && {
                purchase_price: storedPurchasePriceRef.current,
              }),
            ...(storedApprovalOddsRef.current !== undefined && {
              approval_odds: storedApprovalOddsRef.current,
            }),
            ...(storedApprovalOutcomeRef.current !== undefined && {
              approval_outcome: storedApprovalOutcomeRef.current,
            }),
          });

          // For lead_submitted trigger tabs, fetch approval data from the LendAPI
          // Cloud Function proxy before firing the GA event. The fetch is deduped
          // and cached in refs, so subsequent trigger tabs reuse the value. This
          // runs in a fire-and-forget IIFE so the synchronous control flow below
          // (login modal trigger) is not blocked.
          if (isLeadSubmittedTriggerTab(trackingTabName) && sessionIdRef.current) {
            (async () => {
              await ensureApprovalDataLoaded();
              processPerformanceTracking(
                trackingTabName,
                productConfig,
                productType,
                buildEnhancedPayload()
              );
            })().catch((err) => {
              console.error('[LendAPI Combined] lead_submitted tracking IIFE error:', err);
            });
          } else {
            processPerformanceTracking(
              trackingTabName,
              productConfig,
              productType,
              buildEnhancedPayload()
            );
          }
        }

        // Check if we need to show login modal on this tab
        if (
          prequalCompleteForcedLoginConfig?.enabled &&
          eventType === 'pageLoad' &&
          tabName &&
          prequalCompleteForcedLoginConfig.showBeforeTabs.some(
            (t) => normalizeTabName(t) === normalizeTabName(tabName)
          ) &&
          !prequalCompleteForcedLoginTriggered.current
        ) {
          prequalCompleteForcedLoginTriggered.current = true;
          const { currentUser } = auth;
          const userIsAuthenticated = currentUser && !currentUser.isAnonymous;

          if (!userIsAuthenticated) {
            console.log('[LendAPICombined] Showing forced login modal for tab:', tabName);
            setShowForcedLoginModal(true);
            if (prequalCompleteForcedLoginConfig.blur) {
              setIsBlurred(true);
            }
          } else {
            console.log(
              '[LendAPICombined] User already authenticated, creating loan without modal'
            );
            handleLoginSuccess();
          }
        }
      } catch (error) {
        console.error('[LendAPI Combined] Error handling postMessage:', error);
      }
    };

    window.addEventListener('message', handlePostMessage);

    return () => {
      window.removeEventListener('message', handlePostMessage);
    };
    // productType, productConfig, and callbacks are route-based and cannot change after component mounts (route changes unmount the component)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!urlProductType || !productId || !productSlug) {
    const errors: string[] = [];
    if (!urlProductType) errors.push('Missing productType parameter');
    if (!productId) errors.push('Missing productId parameter');
    if (!productSlug) errors.push('Missing productSlug parameter');
    console.error(
      `[LendAPI Combined Funnel] Missing required route parameters. Errors: ${errors.join(', ')}`
    );
    return <PageNotFound showLogin={false} />;
  }

  const validation = validateLendAPIRoute(productId, productSlug);

  if (!validation.isValid) {
    console.error(
      `[LendAPI Combined Funnel] Invalid route parameters: productId=${productId}, productSlug=${productSlug}. Errors: ${validation.errors.join(
        ', '
      )}`
    );
    return <PageNotFound showLogin={false} />;
  }

  return (
    <PreQualFunnelLayout
      headerRight={
        <LendAPIFunnelHeaderRight hasSession={hasSession} onLoginSuccess={handleLoginSuccess} />
      }
      centerLogo={!hasSession && !isAuthenticated}
      hideChrome={isEmbeddedModalRef.current}
      iconTexts={isApplicationPortion ? LENDAPI_APPLICATION_HEADER_VALUE_PROPS : []}
      mobileVisibleIndex={isApplicationPortion ? 1 : undefined}
    >
      {resumeCheckPending && (
        <Modal visible footer={null} closable={false} centered>
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ fontSize: '16px', marginBottom: '16px' }}>
              Please wait while we load your application
            </p>
            <Spin size="large" />
          </div>
        </Modal>
      )}
      <Modal visible={submitting} footer={null} closable={false} centered>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <p style={{ fontSize: '16px', marginBottom: '16px' }}>Submitting your application...</p>
          <Spin size="large" />
        </div>
      </Modal>
      <Modal visible={showBreakoutOverlay} footer={null} closable={false} centered>
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <p style={{ fontSize: '16px', marginBottom: '16px' }}>
            Continuing to your full application...
          </p>
          <Spin size="large" />
        </div>
      </Modal>
      <LendAPILoginModal
        visible={showForcedLoginModal}
        mode="forced"
        email={storedEmailRef.current}
        name={[storedFirstNameRef.current, storedLastNameRef.current].filter(Boolean).join(' ')}
        text={
          isResumeLogin ? resumeLinkForcedLoginConfig?.text : prequalCompleteForcedLoginConfig?.text
        }
        onLoginSuccess={handleLoginSuccess}
        onCancel={() => {}}
      />
      <div style={{ filter: isBlurred ? 'blur(5px)' : 'none' }}>
        {!applicationSubmitted && !resumeCheckPending && (
          <LendAPIIframe
            src={iframeSrc}
            iframeRef={iframeRef}
            currentTab={currentTab}
            lastKnownTab={lastKnownTab}
            progressConfig={activeProgressConfig}
            showProgressBar={showProgressBar && !isEmbeddedModalRef.current}
            isFullApp={progressPhase === 'fullApp'}
            inModal={isEmbeddedModalRef.current}
          />
        )}
      </div>
      {applicationSubmitted && (
        <LendAPISuccessPage webhookResult={webhookResult} source={sourceIdFromUrlRef.current} />
      )}
    </PreQualFunnelLayout>
  );
};

export default React.memo(LendAPICombinedFunnel);
